import React, { useState } from "react";
import {
    Box,
    TextField,
    IconButton,
    Typography,
    Divider,
    MenuItem,
    Button,
    DialogActions,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import Grid from "@mui/material/Grid2";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

import { Batch } from "./BatchSection";

export interface PartDetail {
    id: string;
    aliasName: string;
    partId: string;
    partNumber: string;
    hsn?: string;
    uom: string;
    currency: string;
    quantity: number;
    unitPrice: number;
    gstPer?: number;
    totalWithGst?: number;
    totalWithoutGst?: number;
    deliveryDate?: string;
    batches?: Batch[];
}

interface Props {
    hsn: string;
    deliveryType: string;
    gstApply: string;
    gstTypeValue: "Common" | "Part Wise" | "";
    gstPerValue: number;
    currency: string;
    gstPerOptions: number[];
    setGstPerValue: (value: number) => void;
    data: PartDetail;
    showErrors?: boolean;
    onChange: (field: keyof PartDetail, value: string | number) => void;
    onDelete: () => void;
}

const PartDetails: React.FC<Props> = ({
    data,
    showErrors,
    onChange,
    onDelete,
    hsn,
    currency,
    deliveryType,
    gstApply,
    gstTypeValue,
    gstPerValue,
    setGstPerValue,
    gstPerOptions
}) => {
    const [openDeleteConfirm, setOpenDeleteConfirm] = React.useState(false);

    const totalWithoutGst = React.useMemo(() => {
        const qty = Number(data.quantity || 0);
        const price = Number(data.unitPrice || 0);
        return qty * price;
    }, [data.quantity, data.unitPrice]);

    const appliedGst =
        gstTypeValue === "Common" ? gstPerValue : data.gstPer || 0;

    const totalWithGst =
        Number(data.totalWithoutGst || 0) +
        (Number(data.totalWithoutGst || 0) * appliedGst) / 100;

    return (
        <>
            <Box display="flex" justifyContent="space-between">
                <Typography fontSize={"1.2rem"}>{data.aliasName}</Typography>
                <IconButton
                    color="error"
                    onClick={() => setOpenDeleteConfirm(true)}
                >
                    <DeleteIcon />
                </IconButton>
            </Box>
            <Typography variant="body2" mb={2}>
                Part ID: {data.partId}
            </Typography>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 3 }} mb={2}>
                    <TextField
                        label="HSN"
                        fullWidth
                        value={data.hsn}
                        onChange={(e) =>
                            onChange(
                                "hsn",
                                e.target.value.replace(/\D/g, "").slice(0, 8)
                            )
                        }
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField
                        label="Unit Of Measurement"
                        value={data.uom}
                        fullWidth
                        disabled
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField
                        label={
                            <>
                                Quantity <span style={{ color: "red" }}>*</span>{" "}
                            </>
                        }
                        fullWidth
                        value={data.quantity}
                        error={
                            showErrors &&
                            (!data.quantity || Number(data.quantity) <= 0)
                        }
                        helperText={
                            showErrors &&
                            (!data.quantity || Number(data.quantity) <= 0)
                                ? "Required"
                                : ""
                        }
                        onChange={(e) => {
                            const value = e.target.value;

                            if (value === "") {
                                onChange("quantity", "");
                                onChange("totalWithoutGst", "");
                                return;
                            }

                            if (/^\d+(\.\d{0,2})?$/.test(value)) {
                                const qty = Number(value);
                                const unitPrice = Number(data.unitPrice);
                                const totalWithout = qty * unitPrice;

                                onChange("quantity", value);
                                onChange(
                                    "totalWithoutGst",
                                    totalWithout.toFixed(2)
                                );

                                const gst =
                                    gstTypeValue === "Common"
                                        ? gstPerValue
                                        : data.gstPer || 0;
                                const totalWith =
                                    totalWithout + (totalWithout * gst) / 100;
                                onChange("totalWithGst", totalWith.toFixed(2));
                            }
                        }}
                        inputProps={{
                            inputMode: "decimal",
                            pattern: "[0-9]*[.]?[0-9]{0,2}"
                        }}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField
                        label={
                            <>
                                Unit Price{" "}
                                <span style={{ color: "red" }}>*</span>
                            </>
                        }
                        fullWidth
                        value={data.unitPrice}
                        error={
                            showErrors &&
                            (!data.unitPrice || Number(data.unitPrice) <= 0)
                        }
                        helperText={
                            showErrors &&
                            (!data.unitPrice || Number(data.unitPrice) <= 0)
                                ? "Required and must be greater than 0"
                                : ""
                        }
                        inputProps={{
                            inputMode: "decimal",
                            pattern: "[0-9]*[.]?[0-9]{0,2}"
                        }}
                        onChange={(e) => {
                            const value = e.target.value;

                            if (value === "") {
                                onChange("unitPrice", "");
                                onChange("totalWithoutGst", "");
                                return;
                            }

                            if (/^\d+(\.\d{0,2})?$/.test(value)) {
                                const newUnitPrice = Number(value);
                                const quantity = Number(data.quantity || 0);
                                const totalWithout = quantity * newUnitPrice;

                                onChange("unitPrice", value);
                                onChange(
                                    "totalWithoutGst",
                                    totalWithout.toFixed(2)
                                );

                                const gst =
                                    gstTypeValue === "Common"
                                        ? gstPerValue
                                        : data.gstPer || 0;
                                const totalWith =
                                    totalWithout + (totalWithout * gst) / 100;
                                onChange("totalWithGst", totalWith.toFixed(2));
                            }
                        }}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField
                        label={`Total Amount (excluding GST) ${currency === "EUR" ? "€" : currency === "USD" ? "$" : gstApply === "Yes" ? "₹" : ""}`}
                        fullWidth
                        value={totalWithoutGst.toFixed(2)}
                        disabled
                    />
                </Grid>

                {gstTypeValue === "Part Wise" && (
                    <Grid size={{ md: 3, xs: 12 }}>
                        <TextField
                            fullWidth
                            select
                            label={
                                <>
                                    Select GST %{" "}
                                    <span style={{ color: "red" }}>*</span>
                                </>
                            }
                            value={data.gstPer || ""}
                            error={showErrors && !data.gstPer}
                            helperText={
                                showErrors && !data.gstPer ? "Required" : ""
                            }
                            onChange={(e) => {
                                const gst = Number(e.target.value);
                                onChange("gstPer", gst);
                                const totalWithout = Number(
                                    data.totalWithoutGst || 0
                                );
                                const totalWith =
                                    totalWithout + (totalWithout * gst) / 100;

                                onChange("totalWithGst", totalWith.toFixed(2));
                            }}
                        >
                            {gstPerOptions.map((gst) => (
                                <MenuItem key={gst} value={gst}>
                                    {gst}%
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                )}
                {gstApply === "Yes" && (
                    <Grid size={{ xs: 12, sm: 3 }}>
                        <TextField
                            label="Total Amount (including GST) ₹"
                            fullWidth
                            value={totalWithGst.toFixed(2)}
                            disabled
                        />
                    </Grid>
                )}

                {deliveryType === "Staggered partwise" && (
                    <Grid size={{ md: 3, xs: 12 }} mb={2}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                sx={{ minWidth: "100%" }}
                                label={
                                    <>
                                        Delivery Date{" "}
                                        <span style={{ color: "red" }}>*</span>{" "}
                                    </>
                                }
                                minDate={dayjs()}
                                value={
                                    data.deliveryDate
                                        ? dayjs(data.deliveryDate)
                                        : null
                                }
                                onChange={(newValue) =>
                                    onChange(
                                        "deliveryDate",
                                        newValue
                                            ? newValue.format("YYYY-MM-DD")
                                            : ""
                                    )
                                }
                                slotProps={{
                                    textField: {
                                        error: showErrors && !data.deliveryDate,
                                        helperText:
                                            showErrors && !data.deliveryDate
                                                ? "Required"
                                                : "",
                                        fullWidth: true
                                    },
                                    field: {
                                        readOnly: true
                                    }
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>
                )}
            </Grid>

            <Dialog
                open={openDeleteConfirm}
                onClose={() => setOpenDeleteConfirm(false)}
            >
                <DialogTitle>Confirmation</DialogTitle>

                <DialogContent>
                    <DialogContentText>
                        Are you want to delete the part?
                    </DialogContentText>
                </DialogContent>

                <DialogActions>
                    <Button
                        color="error"
                        onClick={() => setOpenDeleteConfirm(false)}
                    >
                        Cancel
                    </Button>

                    <Button
                        color="primary"
                        onClick={() => {
                            onDelete();
                            setOpenDeleteConfirm(false);
                        }}
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default PartDetails;
